import { Box, Button, CircularProgress } from '@mui/material';
import {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getEventAudits } from '@context/inspections/inspectionUtils';

import './index.scss';

interface ClaimHistoryItem {
  name: string;
  email: string;
  createdDate: string;
  message: string;
}

interface ClaimStepsProps {
  claim: any;
  eventId?: string;
  title?: string;
  filterTag?: string;
  reloadToken?: number;
  subtitle?: string;
  columnLabels?: {
    name: string;
    email: string;
    date: string;
    detail: string;
  };
  // Datos de la tabla en gris opaco sin negrita (historial de Análisis / HU-04).
  lightRows?: boolean;
}

const DEFAULT_COLUMN_LABELS = {
  name: 'Nombre',
  email: 'Email',
  date: 'Fecha',
  detail: 'Descripción',
};

// Normaliza para comparar tags sin que las tildes o mayúsculas rompan el match.
const normalizeTag = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'white',
    color: '#6D788B',
    fontStyle: 'normal',
    fontSize: '14px',
    letterSpacing: '0.5px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontWeight: '600',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const History: FunctionComponent<ClaimStepsProps> = ({
  claim,
  eventId,
  title = 'Historial del caso',
  filterTag,
  reloadToken,
  subtitle,
  columnLabels = DEFAULT_COLUMN_LABELS,
  lightRows,
}): JSX.Element => {
  const [historyItems, setHistoryItems] = useState<any[]>(claim || []);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Si hay exactamente 10 registros iniciales, asumimos que puede haber más
  const [hasMore, setHasMore] = useState<boolean>(claim && claim.length === 10);
  const [loading, setLoading] = useState<boolean>(false);
  const [reloading, setReloading] = useState<boolean>(false);
  const loadGenerationRef = useRef<number>(0);

  const formatDate = (dateString: string) => {
    dayjs.extend(utc);
    const date = dayjs(dateString);
    const formattedDate = date
      .utc(true)
      .local()
      .format('D MMMM YYYY, h:mm:ss A');

    return formattedDate;
  };

  const loadMoreAudits = useCallback(async () => {
    if (!eventId || loading || reloading) return;

    const generation = loadGenerationRef.current;
    setLoading(true);
    try {
      const nextPage = currentPage + 1;
      const response = await getEventAudits(eventId, nextPage);

      if (generation !== loadGenerationRef.current) {
        return;
      }

      if (response && response.audits && Array.isArray(response.audits)) {
        setHistoryItems((prev) => [...prev, ...response.audits]);
        setCurrentPage(nextPage);
        setHasMore(response.hasMore !== false); // Asegurar que hasMore sea boolean
      } else {
        console.warn('Invalid response structure:', response);
      }
    } catch (error) {
      console.error('Error loading more audits:', error);
    } finally {
      setLoading(false);
    }
  }, [eventId, currentPage, loading, reloading]);

  // Actualizar historyItems cuando cambie claim
  useEffect(() => {
    if (claim) {
      setHistoryItems(claim);
      setCurrentPage(1); // Resetear a la página 1 cuando cambie el claim
      // Si hay exactamente 10 registros, asumimos que puede haber más
      setHasMore(claim.length === 10);
    }
  }, [claim]);

  useEffect(() => {
    if (!reloadToken || !eventId) {
      return;
    }

    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;

    const reload = async () => {
      setReloading(true);
      try {
        const response = await getEventAudits(eventId, 1);
        if (generation !== loadGenerationRef.current) {
          return;
        }
        if (response && Array.isArray(response.audits)) {
          setHistoryItems(response.audits);
          setCurrentPage(1);
          setHasMore(response.hasMore !== false);
        }
      } catch (error) {
        console.error('Error reloading audits:', error);
      } finally {
        if (generation === loadGenerationRef.current) {
          setReloading(false);
        }
      }
    };

    reload();
  }, [reloadToken, eventId]);

  const visibleItems = useMemo<ClaimHistoryItem[]>(() => {
    if (!filterTag) {
      return historyItems;
    }

    const normalizedTag = normalizeTag(filterTag);

    return historyItems.filter(
      (row) =>
        typeof row?.message === 'string' &&
        normalizeTag(row.message).includes(normalizedTag),
    );
  }, [historyItems, filterTag]);

  // HU-04: en el historial de Análisis las celdas van en gris opaco sin negrita
  // (como el prototipo). El "Historial del caso" global conserva su estilo.
  // HU-04 (historial de Análisis), siguiendo el prototipo: datos en gris neutro
  // sin negrita y fondo blanco; encabezados en negro negrita con fondo rosado
  // tenue; filas sin el alternado gris (zebra). El "Historial del caso" global
  // no se altera (lightRows undefined).
  const bodyCellSx = lightRows
    ? {
        fontWeight: 400,
        color: '#54565a',
        backgroundColor: '#ffffff !important',
      }
    : undefined;

  const headCellSx = lightRows
    ? {
        fontWeight: '700 !important',
        color: '#000000 !important',
        backgroundColor: '#fdeded !important',
      }
    : undefined;

  const rowSx = lightRows
    ? { '&:nth-of-type(odd)': { backgroundColor: '#ffffff' } }
    : undefined;

  return (
    <Box sx={{ paddingTop: '1rem' }}>
      <span className="history-header">
        <label className="history-header__title">{title}</label>
        {subtitle && (
          <span className="history-header__subtitle">{subtitle}</span>
        )}
      </span>
      <TableContainer component={Paper} className="TableContainer">
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={headCellSx}>
                {columnLabels.name}
              </StyledTableCell>
              <StyledTableCell align="left" sx={headCellSx}>
                {columnLabels.email}
              </StyledTableCell>
              <StyledTableCell align="left" sx={headCellSx}>
                {columnLabels.date}
              </StyledTableCell>
              <StyledTableCell align="left" sx={headCellSx}>
                {columnLabels.detail}
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems.length > 0 ? (
              visibleItems.map((row: any, index: any) => (
                <StyledTableRow
                  key={`${index}-${row.name}-${row.createdDate}`}
                  sx={rowSx}
                >
                  <StyledTableCell component="th" scope="row" sx={bodyCellSx}>
                    {row.name}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={bodyCellSx}>
                    {row.email}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={bodyCellSx}>
                    {formatDate(row.createdDate)}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={bodyCellSx}>
                    {row.message}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <StyledTableCell colSpan={4} align="center">
                  <p>Sin información</p>
                </StyledTableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {hasMore && eventId && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={loadMoreAudits}
            disabled={loading || reloading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Cargando...' : 'Ver más'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default History;
